import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import PlusIcon from '../../resources/svg/icon_add_row.svg';
import XIcon from '../../resources/svg/icon_remove_row.svg';
import { AutoCompleteField, Dropdown, TagInput } from '../../components/widgets';
import { CustomMetricsContext } from '../context';
import { DEFAULT_OPERATOR, OPERATOR_TYPES, TRANSLATION_PREFIX } from '../consts';
import { filterByLabelOnly } from './selectFilter';
import css from '../page.css';

const FILTER_METRIC_PLACEHOLDER = `${TRANSLATION_PREFIX}.PLACEHOLDERS.FILTER_METRIC`;
const VALUES_PLACEHOLDER = 'Select expression values';

const expressionErrorsShape = PropTypes.shape({
    dimension: PropTypes.bool,
    operator: PropTypes.bool,
    value: PropTypes.bool,
});

const todayDateString = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

function ExpressionRow({
    expression,
    onChange,
    onRemove,
    onAdd,
    isLast = false,
    removeDisabled = false,
    errors = null,
    showErrors = false,
}) {
    const { dimensions } = useContext(CustomMetricsContext);

    const dimensionOptions = dimensions.map(dimension => ({ value: dimension.name, label: dimension.display_name }));
    const operators = expression.dimension?.operators || [];
    const operatorItems = operators.map(operator => ({ ...operator, display_name: operator.operator }));
    const selectedOperator = operatorItems.find(({ name }) => name === expression.operator.name) || {
        name: 'default',
        display_name: DEFAULT_OPERATOR.operator,
    };

    const onDimensionSelected = option => {
        const newDimension = dimensions.find(({ name }) => name === option.value);
        if (!newDimension) {
            return;
        }

        const oldDimension = expression.dimension;
        const oldOperator = expression.operator;
        const isNewExpression = !oldDimension?.name;
        let newOperator = expression.operator;
        let newValue = expression.value;

        if (!newDimension.operators.some(({ name }) => name === expression.operator.name)) {
            [newOperator] = newDimension.operators;

            if (newOperator.type !== oldOperator.type || isNewExpression) {
                newValue = [];
            }
        }

        if (newOperator.type === OPERATOR_TYPES.LIST && oldDimension?.name !== newDimension.name) {
            newValue = [];
        }

        if (newOperator.type === OPERATOR_TYPES.DATE && typeof newValue !== 'string') {
            newValue = todayDateString();
        }

        onChange({ dimension: newDimension, operator: newOperator, value: newValue });
    };

    const onOperatorSelected = operatorItem => {
        const newOperator = operators.find(({ name }) => name === operatorItem.name);
        if (!newOperator) {
            return;
        }

        let newValue = expression.value;
        if (newOperator.type === OPERATOR_TYPES.DATE && typeof newValue !== 'string') {
            newValue = todayDateString();
        } else if (newOperator.type !== OPERATOR_TYPES.DATE && !Array.isArray(newValue)) {
            newValue = [];
        }

        onChange({ ...expression, operator: newOperator, value: newValue });
    };

    const values = Array.isArray(expression.value) ? expression.value : [];
    const isListOperator = expression.operator.type === OPERATOR_TYPES.LIST;
    const isDateOperator = expression.operator.type === OPERATOR_TYPES.DATE;

    const valueTags = values.map(value => ({
        value: value.name !== undefined ? value.name : value.display_name,
        label: value.display_name,
    }));

    const valueSuggestions = isListOperator
        ? (expression.dimension?.raw_values || []).map(({ name, display_name: displayName }) => ({
              value: name,
              label: displayName,
          }))
        : [];

    const onValueTagsChanged = currentTags => {
        const rawValues = expression.dimension?.raw_values;
        const newValues = (currentTags || []).map(tag => {
            if (rawValues) {
                return (
                    rawValues.find(({ name }) => name === tag.value) || { name: tag.value, display_name: tag.label }
                );
            }

            return { display_name: tag.label };
        });

        onChange({ ...expression, value: newValues });
    };

    const onDateChanged = event => {
        onChange({ ...expression, value: event.target.value });
    };

    return (
        <div className={css.expressionRow}>
            <div className={css.resultField}>
                <AutoCompleteField
                    key={`dimension-${expression.dimension?.name || ''}`}
                    isMulti={false}
                    searchable
                    options={dimensionOptions}
                    value={
                        expression.dimension?.name
                            ? { value: expression.dimension.name, label: expression.dimension.display_name }
                            : null
                    }
                    placeholder={FILTER_METRIC_PLACEHOLDER}
                    onChange={onDimensionSelected}
                    selectOptions={{ filterOption: filterByLabelOnly }}
                    error={showErrors && errors?.dimension ? 'STATIC.REQUIRED_FIELD' : ''}
                />
            </div>
            <div className={css.resultOperator}>
                <Dropdown
                    items={operatorItems}
                    selected={selectedOperator}
                    onSelection={onOperatorSelected}
                    disabled={!expression.dimension?.name}
                    containerStyle={{ width: '100%' }}
                    wrapperStyle={{ width: '100%' }}
                />
                {showErrors && errors?.operator && (
                    <div className={css.errorText}>
                        <Translate id="STATIC.REQUIRED_FIELD" />
                    </div>
                )}
            </div>
            <div className={css.expressionValues}>
                {isDateOperator ? (
                    <input
                        type="date"
                        className={css.dateInput}
                        value={typeof expression.value === 'string' ? expression.value : ''}
                        onChange={onDateChanged}
                    />
                ) : (
                    <TagInput
                        tags={valueTags}
                        suggestions={valueSuggestions}
                        creatable={!isListOperator}
                        hideDropdown={!isListOperator}
                        placeholder={VALUES_PLACEHOLDER}
                        disabled={!expression.dimension?.name}
                        onChange={onValueTagsChanged}
                        containerStyle={{ width: '100%' }}
                        virtualScrolling
                    />
                )}
                {showErrors && errors?.value && (
                    <div className={css.errorText}>
                        <Translate id="STATIC.REQUIRED_FIELD" />
                    </div>
                )}
            </div>
            <div className={css.expressionControls}>
                <button
                    type="button"
                    className={css.iconButton}
                    disabled={removeDisabled}
                    title={removeDisabled ? 'At least one expression is required' : 'Remove expression'}
                    onClick={onRemove}
                >
                    <XIcon className={css.removeIcon} />
                </button>
                {isLast && (
                    <button type="button" className={css.iconButton} title="Add expression" onClick={onAdd}>
                        <PlusIcon className={css.addIcon} />
                    </button>
                )}
            </div>
        </div>
    );
}

ExpressionRow.propTypes = {
    expression: PropTypes.shape({
        dimension: PropTypes.objectOf(PropTypes.any),
        operator: PropTypes.objectOf(PropTypes.any),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.object)]),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    isLast: PropTypes.bool,
    removeDisabled: PropTypes.bool,
    errors: expressionErrorsShape,
    showErrors: PropTypes.bool,
};

export default ExpressionRow;

export const isDefaultSingleExpression = (expression, numberOfExpressions) =>
    numberOfExpressions === 1 &&
    !expression.dimension?.name &&
    !expression.operator?.name &&
    Array.isArray(expression.value) &&
    !expression.value.length;
